import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AgreementAcceptPanel } from "../legal/AgreementAcceptPanel";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { COMPANY_INACTIVE_MESSAGE, isCompanyInactive } from "../RequireSenderCompany";
import AdminNav from "../AdminNav";
import AccountsNav from "../AccountsNav";
import { usePincodeAutofill } from "../../lib/usePincodeAutofill";

const EMPTY_ADDR = { line1: "", line2: "", city: "", state: "", pincode: "" };
const EMPTY_PKG = { quantity: 1, actual_weight_kg_per_box: "", length_cm: "", width_cm: "", height_cm: "", package_type: "CARTON_BOX" };

function errMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return e?.displayMessage || fallback || "Something went wrong";
}

function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <label className="block text-sm">
      <span className="text-neutral-600">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none"
      />
    </label>
  );
}

function AddressFields({ prefix, addr, setAddr }) {
  const set = (k) => (v) => setAddr({ ...addr, [k]: v });
  const { status, error, onPincodeChange } = usePincodeAutofill(addr, setAddr);
  const inp = "mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none";
  const ro = "mt-1 w-full rounded-md border border-neutral-200 bg-neutral-100 px-3 py-2 text-sm text-neutral-600 cursor-not-allowed";
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2"><Field label="Address line 1" value={addr.line1} onChange={set("line1")} testid={`${prefix}-line1`} /></div>
      <label className="block text-sm">
        <span className="text-neutral-600">Pincode</span>
        <input data-testid={`${prefix}-pincode`} inputMode="numeric" maxLength={6} value={addr.pincode ?? ""} onChange={(e) => onPincodeChange(e.target.value)} className={inp} />
        {status === "resolving" && <span data-testid={`${prefix}-pin-resolving`} className="mt-1 block text-xs text-neutral-500">Resolving City/State…</span>}
        {status === "error" && <span data-testid={`${prefix}-pin-error`} className="mt-1 block text-xs text-red-600">{error}</span>}
      </label>
      <div />
      <label className="block text-sm">
        <span className="text-neutral-600">City</span>
        <input data-testid={`${prefix}-city`} value={addr.city ?? ""} readOnly tabIndex={-1} placeholder="Auto from PIN" className={ro} />
      </label>
      <label className="block text-sm">
        <span className="text-neutral-600">State</span>
        <input data-testid={`${prefix}-state`} value={addr.state ?? ""} readOnly tabIndex={-1} placeholder="Auto from PIN" className={ro} />
      </label>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    OPEN: "bg-amber-100 text-amber-800",
    READY: "bg-emerald-100 text-emerald-800",
    CONVERTED: "bg-blue-100 text-blue-800",
    CANCELLED: "bg-neutral-200 text-neutral-600",
  };
  const label = status === "READY" ? "Prepared by Air12 Concierge" : status;
  return <span data-testid="concierge-status-badge" className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${map[status] || "bg-neutral-100"}`}>{label}</span>;
}

/* Shared draft editor used by Sender / Accounts / SA. */
function DraftEditor({ req, onSaved, canEdit, forcePickupReselect }) {
  const d = req.draft || {};
  const [pickup, setPickup] = useState({ ...EMPTY_ADDR, ...(d.pickup_address || {}) });
  const [delivery, setDelivery] = useState({ ...EMPTY_ADDR, ...((d.receiver || {}).delivery_address || {}) });
  const [receiver, setReceiver] = useState({
    name: (d.receiver || {}).name || "", mobile: (d.receiver || {}).mobile || "",
    email: (d.receiver || {}).email || "", company: (d.receiver || {}).company || "",
    gstin: (d.receiver || {}).gstin || "", unloading_contact: (d.receiver || {}).unloading_contact || "",
  });
  const [pkgs, setPkgs] = useState(d.packages && d.packages.length ? d.packages : [{ ...EMPTY_PKG }]);
  const [goods, setGoods] = useState(d.goods_description || "");
  const [commodity, setCommodity] = useState(d.commodity_category || "");
  const [invValue, setInvValue] = useState(d.declared_invoice_value || "");
  const [serviceDate, setServiceDate] = useState(d.service_date || "");
  const [pickupWindow, setPickupWindow] = useState(d.preferred_pickup_window || "");
  const [senderMobile, setSenderMobile] = useState(d.sender_contact_mobile || "");
  const mobileInvalid = senderMobile !== "" && !/^[0-9]{10}$/.test(senderMobile);
  const [saving, setSaving] = useState(false);
  // Pickup Operating Calendar — backend-authoritative Today/Next-Available choices + slots (parity
  // with normal Sender Booking). The frontend never computes working days/holidays/hours/cutoff.
  const [avail, setAvail] = useState(null);
  const [expMsg, setExpMsg] = useState(null);
  const [pickupStale, setPickupStale] = useState(false);

  const setPkg = (i, k, v) => setPkgs(pkgs.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));

  // Fetch availability once the request's route is known; reconcile any stored (possibly stale)
  // pickup selection against the currently offered two-date choices.
  useEffect(() => {
    if (!req.route_code) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/booking/pickup-availability`, { params: { route_code: req.route_code } });
        if (!alive) return;
        setAvail(data);
        const choices = [data.today, data.next_available].filter(Boolean);
        const match = choices.find((c) => c.date === serviceDate);
        const slotOk = match && match.slots.includes(pickupWindow);
        if (serviceDate && !slotOk) {
          setServiceDate(""); setPickupWindow(""); setPickupStale(true);
        }
      } catch (e) { /* availability errors surface at conversion */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req.route_code]);

  // Backend-composed expected-delivery message for the chosen date + slot (rendered verbatim).
  useEffect(() => {
    if (!avail || !serviceDate || !pickupWindow) { setExpMsg(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/booking/pickup-availability`, {
          params: { route_code: req.route_code, service_date: serviceDate, slot: pickupWindow },
        });
        if (alive) setExpMsg(data.expected_delivery?.message || null);
      } catch (e) { if (alive) setExpMsg(null); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avail, serviceDate, pickupWindow]);

  // Conversion returned a stale-pickup reselection (422 pickup_reselect): clear the pickup and show
  // the approved notice near the existing pickup controls. The rest of the draft is untouched.
  useEffect(() => {
    if (forcePickupReselect) { setPickupStale(true); setServiceDate(""); setPickupWindow(""); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcePickupReselect]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        draft_version: req.draft_version,
        pickup_address: pickup,
        receiver: { ...receiver, delivery_address: delivery },
        packages: pkgs.map((p) => ({
          quantity: Number(p.quantity) || 1,
          actual_weight_kg_per_box: Number(p.actual_weight_kg_per_box) || 0,
          length_cm: Number(p.length_cm) || 0, width_cm: Number(p.width_cm) || 0,
          height_cm: Number(p.height_cm) || 0, package_type: p.package_type || "CARTON_BOX",
        })),
        goods_description: goods, commodity_category: commodity,
        declared_invoice_value: invValue === "" ? null : Number(invValue),
        service_date: serviceDate || null,
        preferred_pickup_window: pickupWindow || null,
        sender_contact_mobile: senderMobile || null,
      };
      const { data } = await api.patch(`/concierge/requests/${req.id}/draft`, body);
      toast.success("Draft saved");
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e, "We couldn't save your draft. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="concierge-draft-editor">
      <div>
        <h4 className="mb-2 font-medium">Pickup address</h4>
        <AddressFields prefix="cc-pickup" addr={pickup} setAddr={setPickup} />
        <p className="mt-1 text-xs text-neutral-500">Source airport {req.source_airport} is fixed for this request. A pickup that maps to a different airport needs a new request.</p>
      </div>
      <div>
        <h4 className="mb-2 font-medium">Delivery address</h4>
        <AddressFields prefix="cc-delivery" addr={delivery} setAddr={setDelivery} />
      </div>
      <div>
        <h4 className="mb-2 font-medium">Receiver</h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" value={receiver.name} onChange={(v) => setReceiver({ ...receiver, name: v })} testid="cc-recv-name" />
          <Field label="Mobile" value={receiver.mobile} onChange={(v) => setReceiver({ ...receiver, mobile: v })} testid="cc-recv-mobile" />
          <Field label="Email" value={receiver.email} onChange={(v) => setReceiver({ ...receiver, email: v })} testid="cc-recv-email" />
          <Field label="Receiver GSTIN (optional)" value={receiver.gstin} onChange={(v) => setReceiver({ ...receiver, gstin: v })} testid="cc-recv-gstin" />
        </div>
      </div>
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h4 className="font-medium">Packages</h4>
          <button data-testid="cc-add-pkg" onClick={() => setPkgs([...pkgs, { ...EMPTY_PKG }])} className="text-sm text-blue-700">+ Add package</button>
        </div>
        {pkgs.map((p, i) => (
          <div key={i} className="mb-2 grid grid-cols-5 gap-2" data-testid={`cc-pkg-row-${i}`}>
            <Field label="Qty" value={p.quantity} onChange={(v) => setPkg(i, "quantity", v)} type="number" testid={`cc-pkg-qty-${i}`} />
            <Field label="Wt/box(kg)" value={p.actual_weight_kg_per_box} onChange={(v) => setPkg(i, "actual_weight_kg_per_box", v)} type="number" testid={`cc-pkg-wt-${i}`} />
            <Field label="L(cm)" value={p.length_cm} onChange={(v) => setPkg(i, "length_cm", v)} type="number" testid={`cc-pkg-l-${i}`} />
            <Field label="W(cm)" value={p.width_cm} onChange={(v) => setPkg(i, "width_cm", v)} type="number" testid={`cc-pkg-w-${i}`} />
            <Field label="H(cm)" value={p.height_cm} onChange={(v) => setPkg(i, "height_cm", v)} type="number" testid={`cc-pkg-h-${i}`} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Goods description" value={goods} onChange={setGoods} testid="cc-goods" />
        <Field label="Commodity category" value={commodity} onChange={setCommodity} testid="cc-commodity" />
        <Field label="Declared invoice value (₹)" value={invValue} onChange={setInvValue} type="number" testid="cc-invvalue" />
        <div>
          <Field label="Sender contact mobile" value={senderMobile} onChange={(v) => setSenderMobile(v.replace(/\D/g, "").slice(0, 10))} testid="cc-sendermobile" placeholder="10-digit number" />
          {mobileInvalid && <p className="mt-1 text-xs text-red-600" data-testid="cc-sendermobile-error">Enter exactly 10 digits, or leave it blank for now.</p>}
        </div>
      </div>
      <div data-testid="cc-pickup-section">
        <h4 className="mb-2 font-medium">Pickup date &amp; time</h4>
        <div className="flex flex-col gap-2" data-testid="cc-pickup-date-choices">
          {avail && [avail.today, avail.next_available].filter(Boolean).map((c) => (
            <label key={c.date} className="flex items-center gap-2 text-sm">
              <input type="radio" name={`cc-pickup-${req.id}`} value={c.date}
                checked={serviceDate === c.date}
                onChange={() => { setPickupStale(false); setServiceDate(c.date); setPickupWindow(""); }}
                data-testid={`cc-pickup-date-${c.date}`} />
              <span>{c.label}</span>
            </label>
          ))}
          {!avail && <p className="text-xs text-neutral-500">Loading pickup availability…</p>}
        </div>
        {pickupStale && (
          <p className="mt-1 text-xs text-red-600" data-testid="cc-pickup-stale-notice">
            Your previous pickup time is no longer available. Please select a new pickup time.
          </p>
        )}
        <label className="mt-2 block text-sm">
          <span className="text-neutral-600">Preferred pickup time</span>
          <select data-testid="cc-pickup-time-select" value={pickupWindow} disabled={!serviceDate}
            onChange={(e) => setPickupWindow(e.target.value)}
            className="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-900 focus:outline-none disabled:bg-neutral-100 disabled:text-neutral-400">
            <option value="">{serviceDate ? "Select a pickup time." : "Select a pickup date first."}</option>
            {(() => {
              const c = avail && [avail.today, avail.next_available].filter(Boolean).find((x) => x.date === serviceDate);
              return (c ? c.slots : []).map((s) => (<option key={s} value={s}>{s}</option>));
            })()}
          </select>
        </label>
        {expMsg && <p className="mt-2 text-xs text-neutral-500" data-testid="cc-pickup-expected-delivery">{expMsg}</p>}
        <p className="mt-1 text-xs text-neutral-400">Pickup times are in India Standard Time (IST).</p>
      </div>
      {canEdit && (
        <button data-testid="cc-save-draft" disabled={saving || mobileInvalid} onClick={save}
          className="rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
          {saving ? "Saving…" : "Save draft"}
        </button>
      )}
    </div>
  );
}

function PreviewLine({ req }) {
  const p = req.estimate_preview;
  if (!p) return <p className="text-sm text-neutral-500" data-testid="cc-no-preview">No estimate prepared yet.</p>;
  if (p.status !== "estimate_available") {
    return <p className="text-sm text-amber-700" data-testid="cc-preview-blocked">{p.customer_message || "This request isn't ready to book yet. Please wait for Air12 to prepare it."}</p>;
  }
  const fee = p.concierge_service_fee ?? p.concierge_service_component?.amount ?? 0;
  const total = p.air12_payable_estimate ?? p.air12_service_payable;
  return (
    <div className="text-sm space-y-1" data-testid="cc-preview-breakdown">
      <div className="flex items-center justify-between" data-testid="cc-preview-concierge">
        <span>Concierge Service</span>
        <span className="mono">₹{Number(fee).toLocaleString("en-IN")}</span>
      </div>
      {p.gst_amount != null && (
        <div className="flex items-center justify-between" data-testid="cc-preview-gst">
          <span>GST{p.gst_rate != null ? ` (${p.gst_rate}%)` : ""}</span>
          <span className="mono">₹{Number(p.gst_amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
      )}
      {Number(p.round_off) !== 0 && p.round_off != null && (
        <div className="flex items-center justify-between" data-testid="cc-preview-round-off">
          <span>Round Off</span>
          <span className="mono">{Number(p.round_off) < 0 ? "− " : "+ "}₹{Math.abs(Number(p.round_off)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
      )}
      <div className="flex items-center justify-between font-medium" data-testid="cc-preview-payable">
        <span>Air12 service payable <span className="text-neutral-500 font-normal">(incl. GST)</span></span>
        <span className="mono">₹{total}</span>
      </div>
    </div>
  );
}

/* ---------------- Sender panel (create / review / accept / cancel) ---------------- */
export function ConciergeSenderPanel() {
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pickup, setPickup] = useState({ ...EMPTY_ADDR });
  const [delivery, setDelivery] = useState({ ...EMPTY_ADDR });
  const [creating, setCreating] = useState(false);
  const [noGstin, setNoGstin] = useState(false);
  const [gstinId, setGstinId] = useState("");
  const [authorityAffirmed, setAuthorityAffirmed] = useState(false); // Quote-only affirmation
  const [acceptingName, setAcceptingName] = useState("");
  const [agreement, setAgreement] = useState(null);
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [agreementError, setAgreementError] = useState(null);
  const [agreementBusy, setAgreementBusy] = useState(false);
  const [pickupReselect, setPickupReselect] = useState(false);
  const navigate = useNavigate();
  // A11 — INACTIVE company: view + cancel existing requests only (no create / edit / accept).
  const { profile } = useAuth();
  const inactive = isCompanyInactive(profile);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/concierge/requests");
      setRequests(data.requests || []);
    } catch (e) { /* silent for empty sender surface */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const key = (crypto?.randomUUID && crypto.randomUUID()) || String(Date.now()) + Math.random();
      await api.post("/concierge/requests", {
        pickup_address: pickup, delivery_address: delivery, create_idempotency_key: key,
      });
      toast.success("Concierge request created. Air12 will prepare it for you.");
      setPickup({ ...EMPTY_ADDR }); setDelivery({ ...EMPTY_ADDR });
      load();
    } catch (e) {
      toast.error(errMsg(e, "We couldn't create your request. Please try again."));
    } finally { setCreating(false); }
  };

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/concierge/requests/${id}`); setSelected(data); setPickupReselect(false); }
    catch (e) { toast.error(errMsg(e, "We couldn't open this request. Please try again.")); }
  };

  const loadAgreement = async (reqId) => {
    setAgreementLoading(true); setAgreementError(null);
    try {
      const { data } = await api.get("/booking/agreement/current", { params: { concierge_request_id: reqId } });
      setAgreement(data);
    } catch (e) { setAgreementError(errMsg(e, "We couldn't load the Air12 Sender Agreement.")); }
    finally { setAgreementLoading(false); }
  };
  useEffect(() => {
    setAgreement(null);
    if (selected?.status === "READY" && !inactive) loadAgreement(selected.id);
  }, [selected?.id, selected?.status]); // eslint-disable-line

  const acceptAgreement = async (name) => {
    setAgreementBusy(true);
    try {
      await api.post("/booking/agreement/accept", {
        agreement_version_id: agreement.version_id, authority_affirmed: true,
        accepting_person_name: name, concierge_request_id: selected.id,
      }, { timeout: 90000 });
      setAgreement((a) => ({ ...a, company_accepted: true }));
      toast.success("Air12 Sender Agreement accepted for your company.");
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det?.message || "The Sender Agreement could not be accepted. Please retry.");
      if (det?.code === "AGREEMENT_VERSION_STALE") loadAgreement(selected.id);
    } finally { setAgreementBusy(false); }
  };

  const convert = async () => {
    try {
      const payable = selected.estimate_preview?.air12_payable_estimate ?? selected.estimate_preview?.air12_service_payable;
      const body = {
        draft_version: selected.draft_version,
        no_gstin: noGstin, company_gstin_id: noGstin ? null : (gstinId || null),
        quote_authority_affirmed: authorityAffirmed,
        accepting_person_name: acceptingName.trim(),
        acceptance_key: (crypto?.randomUUID && crypto.randomUUID()) || String(Date.now()),
        client_quoted_payable: payable != null ? Number(payable) : null,
      };
      const { data } = await api.post(`/concierge/requests/${selected.id}/convert`, body);
      toast.success("Booking created from your Concierge request.");
      setSelected(null); load();
      if (data.shipment_id) navigate(`/sender/shipments/${data.shipment_id}`);
    } catch (e) {
      const det = e?.response?.data?.detail;
      const code = det?.code;
      if (det?.pickup_reselect) {
        // Stale pickup at conversion — keep the same request, show the approved notice near the
        // pickup controls, and let the Sender correct + explicitly retry (no auto-convert).
        toast.error(det.message || "Your previous pickup time is no longer available. Please select a new pickup time.");
        setPickupReselect(true);
      } else if (code === "QUOTE_CHANGED") {
        toast.error("The price changed. Please review the updated amount and confirm again.");
        openDetail(selected.id);
      } else {
        toast.error(errMsg(e, "We couldn't confirm this request. Please try again."));
      }
    }
  };

  const cancel = async (id) => {
    try { await api.post(`/concierge/requests/${id}/cancel`); toast.success("Request cancelled"); setSelected(null); load(); }
    catch (e) { toast.error(errMsg(e, "We couldn't cancel this request. Please try again.")); }
  };

  return (
    <section data-testid="concierge-sender-panel" className="rounded-xl border border-neutral-200 bg-white p-5">
      <h3 className="text-lg font-semibold">Concierge Requests</h3>
      <p className="mt-1 text-sm text-neutral-500">Share just a pickup and delivery address — Air12 Concierge prepares the shipment for you to review and accept.</p>

      {!selected && (
        <>
          {inactive ? (
            <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="concierge-company-inactive-note">
              <span className="font-semibold">Your company is currently inactive.</span> {COMPANY_INACTIVE_MESSAGE}
            </div>
          ) : (<>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-neutral-200 p-3">
              <h4 className="mb-2 font-medium">Pickup address</h4>
              <AddressFields prefix="new-pickup" addr={pickup} setAddr={setPickup} />
            </div>
            <div className="rounded-lg border border-neutral-200 p-3">
              <h4 className="mb-2 font-medium">Delivery address</h4>
              <AddressFields prefix="new-delivery" addr={delivery} setAddr={setDelivery} />
            </div>
          </div>
          <button data-testid="concierge-create-btn" disabled={creating} onClick={create}
            className="mt-3 rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {creating ? "Creating…" : "Request Concierge"}
          </button>
          </>)}

          <div className="mt-6">
            <h4 className="mb-2 font-medium">Your active requests</h4>
            {requests.length === 0 && <p className="text-sm text-neutral-500" data-testid="concierge-empty">No active Concierge requests.</p>}
            <ul className="space-y-2">
              {requests.map((r) => (
                <li key={r.id} data-testid={`concierge-row-${r.id}`}
                  className="flex items-center justify-between rounded-lg border border-neutral-200 px-3 py-2">
                  <span className="text-sm">{r.source_airport} → {r.destination_airport} <StatusBadge status={r.status} /></span>
                  <button data-testid={`concierge-open-${r.id}`} onClick={() => openDetail(r.id)} className="text-sm text-blue-700">Review</button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {selected && (
        <div className="mt-4" data-testid="concierge-detail">
          <button data-testid="concierge-back" onClick={() => setSelected(null)} className="mb-3 text-sm text-blue-700">← Back</button>
          <div className="mb-3 flex items-center gap-2"><StatusBadge status={selected.status} /><span className="text-sm text-neutral-500">{selected.source_airport} → {selected.destination_airport}</span></div>
          <PreviewLine req={selected} />
          <div className="mt-4"><DraftEditor req={selected} onSaved={(d) => { setSelected(d); setPickupReselect(false); }} canEdit={!inactive && (selected.status === "OPEN" || selected.status === "READY")} forcePickupReselect={pickupReselect} /></div>

          {selected.status === "READY" && !inactive && (
            <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="concierge-accept-box">
              <h4 className="font-medium">Accept &amp; create your booking</h4>
              <label className="mt-2 flex items-center gap-2 text-sm" data-testid="cc-nogstin-wrap">
                <input type="checkbox" data-testid="cc-nogstin" checked={noGstin} onChange={(e) => setNoGstin(e.target.checked)} /> No GSTIN
              </label>
              {!noGstin && <Field label="Company GSTIN id" value={gstinId} onChange={setGstinId} testid="cc-gstin-id" />}
              {!agreement?.company_accepted ? (
                <div className="mt-3 rounded border border-neutral-200 bg-white p-3" data-testid="cc-agreement-gate">
                  <AgreementAcceptPanel agreement={agreement} loading={agreementLoading} error={agreementError}
                                        onRetry={() => loadAgreement(selected.id)} onAccept={acceptAgreement}
                                        busy={agreementBusy} testIdPrefix="cc-agreement" />
                </div>
              ) : (
                <>
                  <p className="mt-2 text-xs text-neutral-600" data-testid="cc-agreement-accepted">Your company has accepted the Air12 Sender Agreement (v{agreement.version}) that governs this quote.</p>
                  <Field label="Full legal name of the authorised person accepting this Quote" value={acceptingName} onChange={(v) => setAcceptingName(v.replace(/[^A-Za-z. ]/g, ""))} testid="cc-accepting-name" />
                  <label className="mt-2 flex items-center gap-2 text-sm" data-testid="cc-authority-wrap">
                    <input type="checkbox" data-testid="cc-authority" checked={authorityAffirmed} onChange={(e) => setAuthorityAffirmed(e.target.checked)} /> I confirm that I am authorised to accept this Quote on behalf of {agreement.company_legal_name}.
                  </label>
                </>
              )}
              <div className="mt-3 flex gap-3">
                <button data-testid="concierge-convert-btn" onClick={convert} disabled={!agreement?.company_accepted || !authorityAffirmed || !/^[A-Za-z. ]+$/.test(acceptingName.trim())} className="rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">Accept Quote</button>
                <button data-testid="concierge-cancel-btn" onClick={() => cancel(selected.id)} className="rounded-md border border-neutral-300 px-4 py-2 text-sm">Cancel request</button>
              </div>
            </div>
          )}
          {(selected.status === "OPEN" || (selected.status === "READY" && inactive)) && (
            <button data-testid="concierge-cancel-btn" onClick={() => cancel(selected.id)} className="mt-4 rounded-md border border-neutral-300 px-4 py-2 text-sm">Cancel request</button>
          )}
        </div>
      )}
    </section>
  );
}

/* ---------------- Staff page (Accounts assigned / SA all) ---------------- */
export function ConciergeStaffPage() {
  const { profile } = useAuth();
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/concierge/requests"); setRequests(data.requests || []); }
    catch (e) { toast.error(errMsg(e, "Could not load Concierge queue")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/concierge/requests/${id}`); setSelected(data); }
    catch (e) { toast.error(errMsg(e, "We couldn't open this request. Please try again.")); }
  };

  const markReady = async () => {
    try {
      const { data } = await api.post(`/concierge/requests/${selected.id}/ready`, { draft_version: selected.draft_version });
      toast.success("Marked Ready for Sender Review");
      setSelected(data); load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det?.message || errMsg(e, "Could not mark ready"));
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6" data-testid="concierge-staff-page">
      <AdminNav className="mb-4" />
      <AccountsNav className="mb-4" />
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Air12 Concierge</h1>
        {profile?.role !== "ACCOUNTS" && (
          <button onClick={() => navigate(-1)} className="text-sm text-blue-700" data-testid="concierge-staff-back">← Back</button>
        )}
      </div>
      <p className="mb-4 text-sm text-neutral-500">
        {profile?.role === "SUPER_ADMIN" ? "All Concierge requests across all airports." : "Concierge requests assigned to you."}
      </p>

      {!selected && (
        <>
          {loading && <p className="text-sm text-neutral-500">Loading…</p>}
          {!loading && requests.length === 0 && <p className="text-sm text-neutral-500" data-testid="concierge-staff-empty">No active Concierge requests.</p>}
          <ul className="space-y-2">
            {requests.map((r) => (
              <li key={r.id} data-testid={`concierge-staff-row-${r.id}`}
                className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white px-3 py-2">
                <span className="text-sm">{r.source_airport} → {r.destination_airport} <StatusBadge status={r.status} /></span>
                <button data-testid={`concierge-staff-open-${r.id}`} onClick={() => openDetail(r.id)} className="text-sm text-blue-700">Open</button>
              </li>
            ))}
          </ul>
        </>
      )}

      {selected && (
        <div className="rounded-xl border border-neutral-200 bg-white p-5" data-testid="concierge-staff-detail">
          <button data-testid="concierge-staff-detail-back" onClick={() => setSelected(null)} className="mb-3 text-sm text-blue-700">← Back to queue</button>
          <div className="mb-3 flex items-center gap-2"><StatusBadge status={selected.status} /><span className="text-sm text-neutral-500">{selected.source_airport} → {selected.destination_airport}</span></div>
          <PreviewLine req={selected} />
          {selected.company_inactive && (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="concierge-staff-company-inactive-note">
              <span className="font-semibold">This company is currently inactive.</span> Its Concierge requests can only be viewed or cancelled.
            </div>
          )}
          <div className="mt-4"><DraftEditor req={selected} onSaved={setSelected} canEdit={!selected.company_inactive && (selected.status === "OPEN" || selected.status === "READY")} /></div>
          {!selected.company_inactive && (selected.status === "OPEN" || selected.status === "READY") && (
            <button data-testid="concierge-ready-btn" onClick={markReady}
              className="mt-4 rounded-md bg-emerald-700 px-4 py-2 text-sm font-medium text-white">
              Ready for Sender Review
            </button>
          )}
        </div>
      )}
    </div>
  );
}
